import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Bell,
  CalendarDays,
  ChevronRight,
  CircleHelp,
  CreditCard,
  EllipsisVertical,
  LogOut,
  MessageCircleMore,
  Settings,
  ShieldCheck,
  Moon,
  Sun,
  User,
} from "lucide-react";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import AddNewBtn from "./AddNewBtn";

interface MobileMenuProps {
  hasUnreadMessages: boolean;
  hasUnread: boolean;
  hasUnreadActivity?: boolean;
  onOpenRecentActivity?: () => void;
  showOnDesktop?: boolean;
  includeAccountActions?: boolean;

  currentUserId?: string;
  userName?: string;
  roleName?: string;

  showBilling?: boolean;
  showSettings?: boolean;

  isSuperAdmin?: boolean;
  impersonatedUserId?: string | null;
  onImpersonate?: (targetUserId: string) => Promise<void> | void;

  onSignOut?: () => void;
  canUseDarkMode?: boolean;
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  hasUnreadMessages,
  hasUnread,
  hasUnreadActivity = false,
  onOpenRecentActivity,
  showOnDesktop = false,
  includeAccountActions = true,
  currentUserId,
  userName,
  roleName,
  showBilling = false,
  showSettings = false,
  isSuperAdmin = false,
  impersonatedUserId,
  onImpersonate,
  onSignOut,
  canUseDarkMode = false,
  isDarkMode = false,
  onToggleDarkMode,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [targetUserId, setTargetUserId] = useState("");
  const [isImpersonating, setIsImpersonating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const goTo = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleImpersonate = async (event: React.FormEvent) => {
    event.preventDefault();

    const userId = targetUserId.trim();
    if (!userId || !onImpersonate || isImpersonating) return;

    try {
      setIsImpersonating(true);
      await onImpersonate(userId);
      setIsOpen(false);
    } finally {
      setIsImpersonating(false);
    }
  };

  const menuItemClass =
    "group flex w-full items-center gap-3 rounded-xl border-none bg-transparent px-3 py-2.5 text-left text-[13px] font-medium text-slate-700 dark:text-slate-200 no-underline transition-colors hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-[#7E57C2] dark:hover:text-purple-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30";

  const iconClass =
    "shrink-0 text-slate-400 transition-colors group-hover:text-[#7E57C2]";

  return (
    <div
      ref={menuRef}
      className={`relative z-50 ${
        showOnDesktop ? "block" : "block md:hidden"
      }`}
    >
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`flex h-9 w-9 items-center justify-center rounded-full border-none bg-transparent p-0 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30 ${
          isOpen
            ? "bg-purple-50 dark:bg-purple-400/10 text-[#7E57C2] dark:text-purple-300"
            : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-[#7E57C2]"
        }`}
        aria-label="Open header menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <EllipsisVertical size={19} strokeWidth={2.2} />
      </button>

      <div
        className={`theme-scrollbar dark-popup absolute right-0 top-[calc(100%+10px)] max-h-[min(78dvh,44rem)] w-[min(19rem,calc(100vw-1.5rem))] origin-top-right overflow-y-auto rounded-2xl border border-slate-200/80 bg-white dark:bg-slate-900 p-2 shadow-[0_12px_36px_rgba(15,23,42,0.14)] transition-all duration-300 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)] ${
          isOpen
            ? "visible translate-y-0 scale-100 opacity-100"
            : "invisible -translate-y-3 scale-75 opacity-0 pointer-events-none"
        }`}
        role="menu"
      >
        {includeAccountActions && (currentUserId || userName) && (
          <>
            <button
              type="button"
              onClick={() => goTo("/profile")}
              className="flex w-full items-center gap-3 rounded-xl border-none bg-slate-50/80 dark:bg-slate-800 px-3 py-3 text-left transition-colors hover:bg-purple-50/70 dark:hover:bg-purple-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7E57C2]/30"
            >
              <div className="dark-surface-muted h-10 w-10 shrink-0 overflow-hidden rounded-full bg-white dark:bg-slate-700 ring-1 ring-slate-200 dark:ring-slate-600">
                {currentUserId ? (
                  <AvatarImage userId={currentUserId} size="sm" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-400">
                    <User size={18} />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                {userName && (
                  <p className="m-0 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {userName}
                  </p>
                )}
                {roleName && (
                  <p
                    className={`m-0 mt-0.5 truncate text-[10px] font-bold uppercase tracking-wider ${
                      impersonatedUserId ? "text-red-500" : "text-slate-500"
                    }`}
                  >
                    {roleName}
                  </p>
                )}
              </div>

              <ChevronRight size={16} className="shrink-0 text-slate-400" />
            </button>

            <div className="mx-2 my-2 h-px bg-slate-100 dark:bg-slate-800" />
          </>
        )}

        <div className="flex flex-col gap-0.5">
          {!isSuperAdmin && <>
          <div className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Create
          </div>

          <AddNewBtn
            variant="menu"
            onActionTriggered={() => setIsOpen(false)}
          />

          <div className="mx-2 my-2 h-px bg-slate-100 dark:bg-slate-800" />

          </>}

          <div className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Quick access
          </div>

          <button
            type="button"
            onClick={() => goTo("/calendar")}
            className={menuItemClass}
            role="menuitem"
          >
            <CalendarDays size={17} className={iconClass} />
            <span>Calendar</span>
          </button>

          <button
            type="button"
            onClick={() => goTo("/notification")}
            className={menuItemClass}
            role="menuitem"
          >
            <Bell size={17} className={iconClass} />
            <span>Notifications</span>
            {hasUnread && (
              <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </button>

          {onOpenRecentActivity && (
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onOpenRecentActivity();
              }}
              className={menuItemClass}
              role="menuitem"
            >
              <Activity size={17} className={iconClass} />
              <span>Recent activity</span>
              {hasUnreadActivity && (
                <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[#7E57C2] ring-2 ring-white" />
              )}
            </button>
          )}

          <button
            type="button"
            onClick={() => goTo("/chat")}
            className={menuItemClass}
            role="menuitem"
          >
            <MessageCircleMore size={17} className={iconClass} />
            <span>Chat</span>
            {hasUnreadMessages && (
              <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </button>

          {includeAccountActions && (
            <>
              <div className="mx-2 my-2 h-px bg-slate-100 dark:bg-slate-800" />

              <div className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Account
              </div>

              <button
                type="button"
                onClick={() => goTo("/profile")}
                className={menuItemClass}
                role="menuitem"
              >
                <User size={17} className={iconClass} />
                <span>My Profile</span>
              </button>

              {canUseDarkMode && onToggleDarkMode && (
                <button
                  type="button"
                  onClick={onToggleDarkMode}
                  className={menuItemClass}
                  role="menuitem"
                >
                  {isDarkMode ? (
                    <Sun size={17} className={iconClass} />
                  ) : (
                    <Moon size={17} className={iconClass} />
                  )}
                  <span>{isDarkMode ? "Use light mode" : "Use dark mode"}</span>
                </button>
              )}

              {showBilling && (
                <button
                  type="button"
                  onClick={() => goTo("/dashboard/owner/billing-details")}
                  className={menuItemClass}
                  role="menuitem"
                >
                  <CreditCard size={17} className={iconClass} />
                  <span>Billing</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => goTo("/faqs")}
                className={menuItemClass}
                role="menuitem"
              >
                <CircleHelp size={17} className={iconClass} />
                <span>FAQs</span>
              </button>

              {showSettings && (
                <button
                  type="button"
                  onClick={() => goTo("/settings")}
                  className={menuItemClass}
                  role="menuitem"
                >
                  <Settings size={17} className={iconClass} />
                  <span>Settings</span>
                </button>
              )}

              {isSuperAdmin && !impersonatedUserId && onImpersonate && (
                <>
                  <div className="mx-2 my-2 h-px bg-slate-100 dark:bg-slate-800" />

                  <div className="mx-1 rounded-xl border border-purple-100/70 dark:border-purple-400/20 bg-purple-50/60 dark:bg-purple-400/10 p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <ShieldCheck size={15} className="text-[#7E57C2]" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#7E57C2]">
                        Super Admin Tools
                      </span>
                    </div>

                    <form
                      onSubmit={handleImpersonate}
                      className="flex flex-col gap-2"
                    >
                      <input
                        type="text"
                        value={targetUserId}
                        onChange={(event) => setTargetUserId(event.target.value)}
                        placeholder="Target User ID"
                        className="dark-surface-muted dark-input-borderless w-full rounded-lg border border-purple-200 bg-white dark:bg-slate-800 px-2.5 py-2 text-xs text-slate-800 dark:text-slate-100 outline-none transition-shadow placeholder:text-slate-400"
                      />

                      <button
                        type="submit"
                        disabled={!targetUserId.trim() || isImpersonating}
                        className="w-full rounded-lg border-none bg-[#7E57C2] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#6d47b0] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isImpersonating ? "Switching..." : "Assume Role"}
                      </button>
                    </form>
                  </div>
                </>
              )}

              <div className="mx-2 my-2 h-px bg-slate-100 dark:bg-slate-800" />

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  if (onSignOut) {
                    onSignOut();
                  } else {
                    navigate("/logout");
                  }
                }}
                className="group flex w-full items-center gap-3 rounded-xl border-none bg-transparent px-3 py-2.5 text-left text-[13px] font-medium text-red-600 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200"
                role="menuitem"
              >
                <LogOut
                  size={17}
                  className="shrink-0 text-red-400 transition-colors group-hover:text-red-600"
                />
                <span>Sign Out</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
